import { useState } from "react";
import { Loader2, Rocket, FileText } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import EinmalkaufPanel from "@/components/landing/EinmalkaufPanel";
import { mitHerkunft } from "@/lib/herkunft"; // MESS-07, Betreiberfreigabe 27.10.2026

// V5I-B (V5G §1–§3) — Zwei-Türen-Popup am Ende des anonymen Flows; öffnet erst
// nach ausdrücklichem Klick auf „Dokumentation freigeben" (Welt.jsx).
// Nicht schließbar (kein Später, kein Zurück, kein X): `open` ist kontrolliert,
// es gibt keinen onOpenChange-Handler und ESC wird unterdrückt. Genau zwei Türen:
// Tür 1 Grundprodukt CLARTEXT-Arbeitsschutzmappe (Einzel-PDF, bestehendes
// EinmalkaufPanel), Tür 2 bestehender Demo-Start.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEMO_LAST_KEY = "clartext_lp_demo_at";
const DAY_MS = 24 * 60 * 60 * 1000;

// Exakte Inverse der _rec_label-Abbildung (routes/public.py) — der Rück-Shim
// (_recurrence_code, one_time_print.py) mappt diese Labels wieder auf Codes.
const REC_LABEL_BY_CODE = {
  einmalig: "einmalig",
  monatlich: "monatlich",
  vierteljaehrlich: "vierteljährlich",
  halbjaehrlich: "halbjährlich",
  jaehrlich: "jährlich",
  zwei_jahre: "alle 2 Jahre",
};

// Exakte Inverse von _SUBJECT_TYPE_BY_LABEL (one_time_print.py Z. 20–25).
const TYP_LABEL_BY_SUBJECT_TYPE = {
  machine: "Maschine",
  hazmat: "Gefahrstoff",
  vehicle: "Fahrzeug",
  activity: "Tätigkeit",
};

const recLabel = (code) => REC_LABEL_BY_CODE[code] || code || "";

// V5C §3.1 — clientseitige Spiegelung course → Vorschau-Form (`purchase.content`).
// Exakt die vom Rück-Shim (shim_purchase_content_to_course_form) und vom
// WeasyPrint-Mail-PDF konsumierten Bestandsfelder. KEINE zweite Datenquelle:
// ausschließlich das bereits in Welt.jsx geladene Course-Objekt.
export function buildVorschauContent(course) {
  if (!course || !course.id) return null;
  const content = {
    world_id: course.id,
    title: course.title || "",
    short_description: course.short_description || "",
    is_world: course.is_world !== false,
    duration_minutes: course.duration_minutes ?? 3,
    unterweisung: {
      blocks: (course.content || []).map((b) => ({
        heading: b?.heading || "",
        body: b?.body || "",
      })),
      recurrence: recLabel(course.recurrence),
      recurrence_reason: course.ai_recurrence_reason || "",
    },
    questions: course.questions || [],
    betriebsanweisung: course.betriebsanweisung || null,
    wiederholung: {
      unterweisung: recLabel(course.recurrence),
      ...(course.gbu_recurrence ? { gbu_review: recLabel(course.gbu_recurrence) } : {}),
      ...(course.wartung_recurrence ? { wartung: recLabel(course.wartung_recurrence) } : {}),
    },
  };
  if (TYP_LABEL_BY_SUBJECT_TYPE[course.subject_type]) {
    content.typ = TYP_LABEL_BY_SUBJECT_TYPE[course.subject_type];
  }
  if (course.gbu) {
    content.gbu = {
      responsible_role: course.gbu.responsible_role || "",
      hazards: course.gbu.hazards || [],
      legal_basis: course.legal_basis || [],
    };
  }
  const items = course.wartung?.inspection_items || [];
  if (items.length) {
    content.wartung = {
      items: items.map((it) => ({
        description: it?.description || "",
        interval: it?.interval || "",
        who: it?.who || "",
      })),
      next_check: course.wartung?.next_check_recommended_in || "",
    };
  }
  return content;
}

// V5I-B (V5G §2) — reine Sichtbarkeitslogik (testbar): der Button „Dokumentation
// freigeben" erscheint ausschließlich im anonymen Vorschau-Flow, nie in der
// Druckansicht, nur bei Manifest terminal ∧ readiness ready. Das Popup selbst
// öffnet erst nach Klick auf diesen Button (kein Auto-Open).
export function sollZweiTuerenPopupOeffnen({
  istOeffentlicheVorschau,
  druckansicht,
  released,
  lehrbildTerminal,
  readinessReady,
  bereitsGeoeffnet,
}) {
  return (
    !!istOeffentlicheVorschau &&
    !druckansicht &&
    !released &&
    !!lehrbildTerminal &&
    readinessReady === true &&
    !bereitsGeoeffnet
  );
}

export default function ZweiTuerenPopup({ open, course }) {
  const [demoLaeuft, setDemoLaeuft] = useState(false);
  const [demoFehler, setDemoFehler] = useState(null);
  const content = buildVorschauContent(course);

  // Tür 2 — exakter Kern des bestehenden startDemo-Flows (LandingPage.jsx):
  // derselbe Endpunkt, derselbe localStorage-Vertrag, derselbe Redirect.
  const startDemo = async () => {
    if (demoLaeuft) return;
    try {
      const last = Number(localStorage.getItem(DEMO_LAST_KEY) || 0);
      if (last && Date.now() - last < DAY_MS) {
        setDemoFehler(
          "Sie haben kürzlich bereits einen Testzugang gestartet. Bitte versuchen Sie es später erneut.",
        );
        return;
      }
    } catch {
      /* localStorage nicht verfügbar — Backend-Limit greift trotzdem */
    }
    setDemoLaeuft(true);
    setDemoFehler(null);
    try {
      // AUTH-SICHERHEIT V2 · Phase 2: `credentials: "include"` sorgt dafür,
      // dass das httpOnly-Cookie, das der Demo-Endpoint via Set-Cookie liefert,
      // vom Browser übernommen wird (analog withCredentials im axios-Client).
      const res = await fetch(`${API}/public/demo-start-wizard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(mitHerkunft({})),
      });
      if (!res.ok) {
        setDemoLaeuft(false);
        setDemoFehler(
          res.status === 429
            ? "Zu viele Testzugänge von dieser Verbindung. Bitte versuchen Sie es morgen erneut."
            : "Der Testzugang konnte nicht gestartet werden. Bitte versuchen Sie es später erneut.",
        );
        return;
      }
      const data = await res.json();
      try {
        // AUTH-SICHERHEIT V2 · Phase 2: KEIN token mehr im localStorage.
        // Der Auth-Cookie wurde vom Backend gesetzt; wir persistieren nur
        // die Anzeigedaten der neuen Owner-Session.
        localStorage.setItem(
          "owner_session",
          JSON.stringify({
            tenant_id: data.tenant_id,
            company_name: data.company_name,
            company_code: data.company_code,
            must_change_pin: !!data.must_change_pin,
          }),
        );
        if (data.owner_pin) {
          localStorage.setItem(
            "pending_demo_creds",
            JSON.stringify({
              demo_tenant_id: data.tenant_id,
              company_code: data.company_code,
              owner_pin: data.owner_pin,
            }),
          );
        }
        localStorage.setItem(DEMO_LAST_KEY, String(Date.now()));
      } catch {
        /* Session unkritisch persistierbar — Redirect erfolgt trotzdem */
      }
      window.location.href = "/unternehmer";
    } catch {
      setDemoLaeuft(false);
      setDemoFehler("Verbindungsproblem. Bitte versuchen Sie es später erneut.");
    }
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        data-testid="zwei-tueren-popup"
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="zwei-tueren-titel">
            Ihre Dokumentation ist fertig
          </AlertDialogTitle>
          <AlertDialogDescription data-testid="zwei-tueren-beschreibung">
            Wählen Sie, wie Sie Ihre fertige Dokumentation nutzen möchten.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-4">
          <div
            data-testid="popup-tuer-1"
            className="rounded-lg border border-slate-200 p-4"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Tür 1
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              CLartext-Arbeitsschutzmappe
            </h3>
            <p className="mt-1 text-sm text-slate-600" data-testid="popup-tuer-1-lieferumfang">
              Sie erhalten Ihre vollständige Arbeitsschutzmappe als einmaligen PDF-Download:
            </p>
            <ul className="mt-1 list-disc pl-5 text-sm text-slate-700">
              <li className="flex items-center gap-2 list-none -ml-5">
                <FileText size={14} className="text-slate-400 shrink-0" />
                CLartext-Arbeitsschutzmappe (PDF)
              </li>
            </ul>
            <div className="mt-3">
              <EinmalkaufPanel content={content} />
            </div>
          </div>

          <div
            data-testid="popup-tuer-2"
            className="rounded-lg border border-slate-200 p-4"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Tür 2
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              14 Tage kostenlos testen
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Testzugang mit vollem Funktionsumfang. Keine automatische Verlängerung.
            </p>
            <button
              type="button"
              className="mt-3 inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60"
              data-testid="popup-tuer-2-button"
              onClick={startDemo}
              disabled={demoLaeuft}
            >
              {demoLaeuft ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Rocket size={16} />
              )}
              {demoLaeuft ? "Testzugang wird eingerichtet …" : "14 Tage kostenlos testen"}
            </button>
            {demoFehler && (
              <p className="mt-2 text-sm text-red-600" data-testid="popup-tuer-2-fehler">
                {demoFehler}
              </p>
            )}
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
